import dayjs from 'dayjs'
import { useMemo } from 'react'
import {
  FOUR_WEEK_TRIAL_CUTOFF,
  LEGACY_FREE_TRIAL_DAYS,
  NO_OF_FREE_TRIAL_DAYS,
} from 'v2/pages/billing/plans-and-pricing/constants'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { isOrgCreatedOnOrAfter } from 'v2/shared/utils/org-cutoff'
import { PlanType } from 'v2/types'

export interface UserPlanInfo {
  plan: PlanType | undefined
  isFreePlan: boolean
  isProfessionalPlan: boolean
  isEnterprisePlan: boolean
  /**
   * Whether Enterprise-gated features should render as locked (ghosted label,
   * lock icon, upgrade CTA) for this user. True only for a plan we positively
   * recognise as below Enterprise AND an org created on/after
   * `ENTERPRISE_GATING_CUTOFF` — orgs older than the cutoff are grandfathered
   * and never locked. A still-loading profile, an unknown plan, or a missing
   * org_created_at all fail OPEN rather than hiding features.
   * Use this as the single gate for every "upgrade to Enterprise" lock/CTA.
   */
  isEnterpriseLocked: boolean
  /**
   * Whether the profile behind `plan` has resolved. `isEnterpriseLocked` is
   * false while loading, so anything that *acts* on the lock (a redirect, or
   * mounting a gated tab and firing its query) must wait on this instead of
   * reading the lock alone.
   */
  isPlanLoaded: boolean
  trialDaysLeft: number
  isTrial: boolean
  // Trial expiry (free-trial accounts only): the ISO end date and whether it has
  // passed. Undefined/false for non-trial accounts.
  trialEndDate?: string
  isTrialExpired: boolean
  nextBillingDate?: string
}

/** The plan-tier boolean flags derived purely from the plan type. */
export type PlanFlags = Pick<
  UserPlanInfo,
  'isFreePlan' | 'isProfessionalPlan' | 'isEnterprisePlan' | 'isEnterpriseLocked'
>

/**
 * Enterprise gating only applies to orgs created on or after this date — signups
 * from 2026-07-29 onward are gated, every org that existed before then keeps the
 * access it already had. Gating an integrated customer out of screens they were
 * using is a breaking change that needs its own comms, not a silent release
 * (product call, 2026-07-29: existing customers raised tickets for Messaging logs
 * and WhatsApp after the gating shipped to everyone at once).
 *
 * Moving this date forward re-grandfathers more orgs; moving it back takes access
 * away from live accounts, so treat a decrease as a breaking change.
 */
export const ENTERPRISE_GATING_CUTOFF = '2026-07-29'

/**
 * org_created_at arrives as "YYYY-MM-DD HH:mm:ss". The fixed-width date prefix
 * lets us string-compare against a YYYY-MM-DD cutoff without timezone-parsing
 * ambiguity (same trick as `use-livekit-enabled`). An absent org_created_at is
 * treated as pre-cutoff, i.e. grandfathered — fail open, never lock on missing data.
 */
const isPostGatingCutoffOrg = (orgCreatedAt: string | undefined): boolean =>
  orgCreatedAt ? orgCreatedAt >= ENTERPRISE_GATING_CUTOFF : false

/**
 * Pure derivation of the plan-tier flags from a plan value and the org's creation
 * timestamp. Extracted from the hook so the Enterprise gate (`isEnterpriseLocked`)
 * is unit-testable without a react-query/profile harness.
 *
 * `isEnterpriseLocked` is a positive test against the tiers we know sit below
 * Enterprise, NOT `!== ENTERPRISE`. Both spellings agree on today's three plans,
 * but they diverge on anything unrecognised — a plan the backend adds later, or
 * `undefined` while the profile loads. `!== ENTERPRISE` would lock those out;
 * the allowlist leaves them unlocked. Failing open is right here: this is
 * upsell UX, not a security boundary (the API enforces entitlements), so the
 * worst case is a non-Enterprise org briefly seeing a feature, versus a paying
 * org losing screens because a plan string changed shape.
 *
 * The plan tier flags stay purely plan-derived — only the *lock* is grandfathered,
 * so billing/spend-limit code reading `isEnterprisePlan` is unaffected.
 */
export const derivePlanFlags = (
  plan: PlanType | undefined,
  orgCreatedAt: string | undefined,
): PlanFlags => {
  const isFreePlan = plan === PlanType.FREE_TRIAL
  const isProfessionalPlan = plan === PlanType.PROFESSIONAL
  return {
    isFreePlan,
    isProfessionalPlan,
    isEnterprisePlan: plan === PlanType.ENTERPRISE,
    isEnterpriseLocked: (isFreePlan || isProfessionalPlan) && isPostGatingCutoffOrg(orgCreatedAt),
  }
}

// Pure trial-window computation, extracted so the expiry boundary is unit-testable
// without rendering. Trial ends trialDays after the org was created;
// it's expired once "now" is a full day past that end date.
export const getTrialStatus = (
  orgCreatedAt: string | undefined,
  isTrial: boolean,
  now = dayjs(),
): { trialEndDate?: string; trialDaysLeft: number; isTrialExpired: boolean } => {
  if (!isTrial || !orgCreatedAt) {
    return { trialEndDate: undefined, trialDaysLeft: 0, isTrialExpired: false }
  }
  const trialDays = isOrgCreatedOnOrAfter(orgCreatedAt, FOUR_WEEK_TRIAL_CUTOFF)
    ? NO_OF_FREE_TRIAL_DAYS
    : LEGACY_FREE_TRIAL_DAYS
  const trialEndDate = dayjs(orgCreatedAt).add(trialDays, 'days')
  return {
    trialEndDate: trialEndDate.toISOString(),
    trialDaysLeft: Math.max(0, trialEndDate.diff(now, 'day')),
    isTrialExpired: now.isAfter(trialEndDate, 'day'),
  }
}

/**
 * Hook to get user's plan information.
 * Returns plan type and all derived boolean checks, including trial information.
 */
export const useUserPlan = (): UserPlanInfo => {
  const { data: profile, isFetched: isPlanLoaded } = useProfileQuery()
  const plan = profile?.plan

  const orgCreatedAt = profile?.organizations?.[0]?.org_created_at

  const isTrial = plan === PlanType.FREE_TRIAL

  const { trialEndDate, trialDaysLeft, isTrialExpired } = useMemo(
    () => getTrialStatus(orgCreatedAt, isTrial),
    [orgCreatedAt, isTrial],
  )

  return {
    plan,
    ...derivePlanFlags(plan, orgCreatedAt),
    isPlanLoaded,
    trialDaysLeft,
    isTrial,
    trialEndDate,
    isTrialExpired,
    nextBillingDate: undefined, // TODO: Add next billing date from backend
  }
}
